import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { OrderFilterService } from '../../services/order-filter/order-filter.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OrderDateCreatedEnum, OrderFiltersEnum } from '@app/utils/enums/orders.enum';
import { IFilterChip, OrdersQueryParams } from '@app/utils/interfaces/order.interface';
import {
  DATE_CREATED_FILTER_OPTIONS,
  DELIVERY_TYPE_FILTER_OPTIONS,
  ORDER_FILTERS_CONFIG,
  ORDER_STATUS_FILTER_OPTIONS,
} from '@app/utils/constants/orders.constants';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';

@Component({
  selector: 'app-filter-chips',
  standalone: true,
  imports: [TranslatePipe, IconComponent],
  templateUrl: './filter-chip.component.html',
  styleUrl: './filter-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipComponent {
  public readonly IconName = IconName;

  private readonly ordersFilterService = inject(OrderFilterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filters = toSignal(
    this.ordersFilterService.activeFilters$.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: {} as OrdersQueryParams },
  );

  public chips = computed<IFilterChip[]>(() => {
    const f = this.filters();
    const chips: IFilterChip[] = [];

    const createChip = (
      key: OrderFiltersEnum,
      value: string,
      selectedOption: string,
    ): IFilterChip => ({
      key,
      value,
      label: ORDER_FILTERS_CONFIG.find((v) => v.key === key)?.title ?? '',
      selectedOption,
    });

    f.orderStatuses?.forEach((s) =>
      chips.push(
        createChip(OrderFiltersEnum.STATUS, s, this.getChipLabel(OrderFiltersEnum.STATUS, s)),
      ),
    );

    f.creators?.forEach((c) => chips.push(createChip(OrderFiltersEnum.CREATOR, c, c)));

    f.deliveryTypes?.forEach((d) =>
      chips.push(
        createChip(
          OrderFiltersEnum.DELIVERY_TYPE,
          d,
          this.getChipLabel(OrderFiltersEnum.DELIVERY_TYPE, d),
        ),
      ),
    );

    if (f.dateCreated) {
      const label =
        f.dateCreated === OrderDateCreatedEnum.Custom
          ? `${this.formatDate(f.startDate)} - ${this.formatDate(f.endDate)}`
          : this.getChipLabel(OrderFiltersEnum.DATE_CREATED, f.dateCreated);

      chips.push(createChip(OrderFiltersEnum.DATE_CREATED, f.dateCreated, label));
    }

    return chips;
  });

  public removeChip(chip: IFilterChip): void {
    const current = this.filters();

    const updated = { ...current };

    switch (chip.key) {
      case OrderFiltersEnum.STATUS:
        updated.orderStatuses = updated.orderStatuses?.filter((x) => x !== chip.value) ?? [];
        break;

      case OrderFiltersEnum.CREATOR:
        updated.creators = updated.creators?.filter((x) => x !== chip.value) ?? [];
        break;

      case OrderFiltersEnum.DELIVERY_TYPE:
        updated.deliveryTypes = updated.deliveryTypes?.filter((x) => x !== chip.value) ?? [];
        break;

      case OrderFiltersEnum.DATE_CREATED:
        updated.dateCreated = undefined;
        updated.startDate = '';
        updated.endDate = '';
        break;
    }

    this.ordersFilterService.setFilters(updated);
  }

  public handleResetAll(): void {
    this.ordersFilterService.clearFilters();
  }

  private getChipLabel(key: string, value: string): string {
    if (key === OrderFiltersEnum.DATE_CREATED) {
      const config = DATE_CREATED_FILTER_OPTIONS.find((v) => v.key === value);
      return config?.label ?? '';
    }
    if (key === OrderFiltersEnum.STATUS) {
      const config = ORDER_STATUS_FILTER_OPTIONS.find((v) => v.key === value);
      return config?.label ?? '';
    }
    if (key === OrderFiltersEnum.DELIVERY_TYPE) {
      const config = DELIVERY_TYPE_FILTER_OPTIONS.find((v) => v.key === value);
      return config?.label ?? '';
    }
    return '';
  }

  private formatDate(dateString?: string): string {
    return dateString ? dateString.split('T')[0].replaceAll('-', '/') : '';
  }
}
